import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import { greyP4, greyP5 } from '../../theme';

interface AddSeriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddSeries: (name: string, description?: string) => void;
  manufacturerName?: string;
  editData?: { name: string; description?: string } | null;
}

const AddSeriesModal: React.FC<AddSeriesModalProps> = ({
  isOpen,
  onClose,
  onAddSeries,
  manufacturerName: _manufacturerName,
  editData,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDescription('');
    } else if (editData) {
      setName(editData.name);
      setDescription(editData.description || '');
    }
  }, [isOpen, editData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onAddSeries(name.trim(), description.trim() || undefined);
      setName('');
      setDescription('');
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-series-modal-title"
        className="w-full max-w-md rounded-lg p-6"
        style={{ backgroundColor: greyP5 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 id="add-series-modal-title" className="text-lg font-semibold text-white">
            {editData ? t('catalogue.editSeriesTitle') : t('catalogue.addSeriesTitle')}
          </h2>
          <button
            onClick={onClose}
            className="text-greyP3 hover:text-white transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm text-greyP3 mb-2">{t('catalogue.seriesName')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('catalogue.seriesNamePlaceholder')}
              className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                border focus:border-green01 focus:outline-none transition-colors"
              style={{ borderColor: greyP4 }}
              autoFocus
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm text-greyP3 mb-2">
              {t('catalogue.description')} ({t('common.optional')})
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('catalogue.descriptionPlaceholder')}
              rows={3}
              className="w-full px-4 py-3 rounded-lg bg-black text-white placeholder-greyP3
                border focus:border-green01 focus:outline-none transition-colors resize-none"
              style={{ borderColor: greyP4 }}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-full text-white text-sm hover:bg-greyP4 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-6 py-2 rounded-full bg-green01 text-black text-sm font-medium
                hover:bg-green03 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {editData ? t('common.save') : t('catalogue.addSeries')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSeriesModal;
